// 261010 Epic 2 / P2c-2 — CHÍNH SÁCH hàng "Máy tự chạy" của khung mới (thuần: không antd/router, jest chạy được).
// Quyết HIỂN THỊ / BẬT / LÝ DO cho hai nút "Chạy chặng 1/2", dòng trạng thái lượt chạy gần nhất, và câu hiện sau khi duyệt storyboard (`autoPrompt`).
// Luật bật: nút ghi plan ⇒ dùng CHUNG `planWriteKindsOf` với trình sửa tay P2b-3 (một nguồn danh sách việc ghi plan); chặng 2 chỉ bật khi Kịch bản READY.
// KHÔNG hỏi xác nhận (chỉ AI chữ — đúng luật mẫu P1 v6: chỉ gen ảnh / video mới hỏi) và KHÔNG hiện giá.
import type { FilmRunningJob } from '../../types/film';
import type { AutoPrompt, RunSummary, ScriptGate, WorkflowRuns, WorkflowStepState } from '../../types/filmWorkflow';
import { truncateText } from './workflowBoardUtils';
import { planWriteKindsOf, planWriteLabelOf, type ActionState } from './workflowActionPolicy';

export const RUN_STAGE_1 = 'RUN_STAGE_1';
/** "Chạy chặng 2" thuần (POST workflow/run): luật cũ khi BE chưa có `gates`, hoặc chạy lại khi kịch bản ĐÃ duyệt (G4). */
export const RUN_STAGE_2 = 'RUN_STAGE_2';
/** 261010 P8a — nút chính khi chưa duyệt / duyệt đã cũ: duyệt kịch bản rồi chạy tiếp chặng 2 (một cú bấm, không hộp xác nhận — chỉ AI chữ). */
export const APPROVE_RUN_STAGE_2 = 'APPROVE_RUN_STAGE_2';
/** Nút phụ: chỉ ghi duyệt, chưa chạy (cho plan đã chạy chặng 2 trước khi có cổng). */
export const APPROVE_ONLY = 'APPROVE_ONLY';
export const REVOKE_APPROVAL = 'REVOKE_APPROVAL';
export type RunStage = 1 | 2;

export interface RunBarCtx {
  isSky: boolean;
  /** Trạng thái bước 1 Kịch bản của plan (từ `workflow-status.steps`). */
  scriptState: WorkflowStepState;
  jobs: Array<Pick<FilmRunningJob, 'type' | 'eventId' | 'key'> & { phase?: string }>;
  /** Chặng đang gửi yêu cầu (chưa có job): khoá nút ngay, khỏi bấm đúp. */
  sending?: RunStage | null;
  /** 261010 P8a — `status.gates?.script`. VẮNG (BE cũ / đọc lỗi) ⇒ luật cũ (G1): nút "Chạy chặng 2" bật theo bước 1 READY, không có nút duyệt. */
  gate?: ScriptGate;
}

const NOTE_MAX = 300;
export const STAGE3_TEXT = 'Chặng 3 — từ Sơ đồ trở đi: người bấm từng bước (tạo ảnh / video luôn hỏi xác nhận).';
const STAGE_DESC: Record<RunStage, string> = {
  1: 'Máy viết thoại, chốt địa điểm và tính thời lượng cho mọi video. Chỉ dùng AI chữ, không tạo ảnh hay video; xong thì dừng.',
  2: 'Máy dàn shot (Đạo diễn), thêm hướng nhìn (Quay phim), soát liền mạch và dàn cảnh. Chỉ dùng AI chữ; xong thì dừng ở các thẻ gắn cờ để bạn xem.',
};
const STAGE_NAME: Record<RunStage, string> = { 1: 'Kịch bản', 2: 'Đạo diễn ⇒ Quay phim' };
const STAGE1_NOT_DONE = 'Chặng 1 chưa xong — cần có thoại và số giây ở mọi video (bước 1 Kịch bản phải sẵn sàng) trước khi chạy chặng 2.';

const STAGE1_RUNNING = 'Đang chạy chặng 1…';
const GATED_DESC = 'Chạy sau khi bạn duyệt kịch bản.';

/**
 * Bảng trạng thái G (261010 P8a) của thẻ Chặng 2, theo `scriptState` + `gate`:
 *   G0 không Sky · G1 `gate` vắng (luật cũ) · G2 NOT_READY/ERROR · G3 READY chưa duyệt · G4 APPROVED · G5 STALE · G6 bận (việc cấp plan / đang gửi) · G7 chặng 1 RUNNING.
 * Kết quả: `[chặng 1, nút CHÍNH chặng 2, ...nút phụ]` — hai phần tử đầu giữ vị trí như trước P8a.
 */
type Phase = 'G1' | 'G2' | 'G3' | 'G4' | 'G5' | 'G7';
const phaseOf = (ctx: RunBarCtx): Phase => {
  if (!ctx.gate) return 'G1';
  switch (ctx.scriptState) {
    case 'RUNNING': return 'G7';
    case 'READY': return 'G3';
    case 'APPROVED': return 'G4';
    case 'STALE': return 'G5';
    default: return 'G2';
  }
};

/** Hai nút chặng + (khi có `gate`) nút phụ duyệt / huỷ. Ẩn hẳn khi không Sky. */
export function runStageActions(ctx: RunBarCtx): ActionState[] {
  const kinds = planWriteKindsOf(ctx.jobs);
  const blocked = kinds.length > 0 || !!ctx.sending;
  const blockReason = kinds.length
    ? `Đang chạy lượt AI trên cả plan (${kinds.map(planWriteLabelOf).join(', ')}) — đợi xong rồi chạy.`
    : ctx.sending ? 'Đang gửi yêu cầu — đợi một chút.' : undefined;
  const phase = phaseOf(ctx);
  const mk = (key: string, label: string, enabled: boolean, reason?: string): ActionState =>
    ({ key, label, visible: ctx.isSky, enabled, confirm: 'NONE', ...(enabled ? {} : { reason }) });
  const stage1 = mk(RUN_STAGE_1, 'Chạy chặng 1', !blocked, blockReason);
  if (phase === 'G1') {
    // Luật cũ y nguyên: chặng 2 chỉ bật khi Kịch bản READY.
    return [stage1, mk(RUN_STAGE_2, 'Chạy chặng 2', !blocked && ctx.scriptState === 'READY', blocked ? blockReason : STAGE1_NOT_DONE)];
  }
  // Nhãn + khoá của nút chính theo trạng thái duyệt (giữ nguyên khi bị khoá G6 để người xem biết sẽ làm gì).
  const [key, label] = phase === 'G4' ? [RUN_STAGE_2, 'Chạy chặng 2'] : phase === 'G5' ? [APPROVE_RUN_STAGE_2, 'Duyệt lại và chạy tiếp'] : [APPROVE_RUN_STAGE_2, 'Duyệt và chạy tiếp'];
  const why = phase === 'G7' ? STAGE1_RUNNING : phase === 'G2' ? STAGE1_NOT_DONE : undefined;
  const off = blocked ? blockReason : why;
  const primary = mk(key, label, !blocked && !why, off);
  // Nút phụ theo duyệt hiện có. G2 không có gì để duyệt ⇒ không có nút phụ.
  const ap = ctx.gate!.approval;
  const kind: 'NONE' | 'OK' | 'OUTDATED' = phase === 'G4' ? 'OK' : phase === 'G5' ? 'OUTDATED' : phase === 'G7' ? (ap ? (ap.matches ? 'OK' : 'OUTDATED') : 'NONE') : 'NONE';
  const sec: ActionState[] = [];
  if (phase !== 'G2') {
    const sOn = !blocked && phase !== 'G7';
    const sReason = blocked ? blockReason : STAGE1_RUNNING;
    if (kind !== 'OK') sec.push(mk(APPROVE_ONLY, kind === 'OUTDATED' ? 'Chỉ duyệt lại' : 'Chỉ duyệt, chưa chạy', sOn, sReason));
    if (kind !== 'NONE') sec.push(mk(REVOKE_APPROVAL, 'Huỷ duyệt', sOn, sReason));
  }
  return [stage1, primary, ...sec];
}

/** Chặng nào đang chạy (theo `phase` của job `FILM_WORKFLOW_RUN`); không có ⇒ null. */
export function runningStageOf(jobs: Array<Pick<FilmRunningJob, 'type'> & { phase?: string }>): RunStage | null {
  const j = jobs.find((x) => x.type === 'FILM_WORKFLOW_RUN');
  return j?.phase === 'stage-1' ? 1 : j?.phase === 'stage-2' ? 2 : null;
}

const PASS_LABEL: Record<string, string> = {
  dialogue: 'Viết thoại', breakdown: 'Chốt hình học địa điểm', durations: 'Tính thời lượng', split: 'Tự chia video vượt trần', direct: 'Đạo diễn (dàn shot)', cinematography: 'Quay phim',
  'numbered-cast': 'Kiểm vai đánh số', continuity: 'Soát liền mạch', staging: 'Dàn cảnh', 'promote-cast': 'Đưa nhân vật phụ vào Bank',
  'fill-appearance': 'Điền ngoại hình nhân vật phụ', 'ensure-looks': 'Tạo bộ đồ mặc định', 'style-profile': 'Đề xuất hồ sơ phong cách',
};
const pad = (n: number) => String(n).padStart(2, '0');
const clockOf = (iso: string | null | undefined): string | null => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? `${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}` : null;
};

/** Dòng trạng thái của một chặng: đang chạy / chưa chạy / xong lúc … / lỗi ở bước X: chữ lỗi. */
export function runSummaryText(stage: RunStage, summary: RunSummary | undefined, o: { running?: boolean; progressLabel?: string; progressNo?: [number, number] } = {}): string {
  if (o.running) return o.progressLabel ? `Đang chạy — ${o.progressLabel}${o.progressNo ? ` (${o.progressNo[0]}/${o.progressNo[1]})` : ''}` : 'Đang chạy…';
  if (!summary) return 'Chưa chạy';
  if (summary.status === 'FAILED') {
    const err = summary.error || 'không rõ lý do';
    return summary.failedPass ? `Lỗi ở bước "${PASS_LABEL[summary.failedPass] ?? summary.failedPass}": ${err}` : `Lỗi: ${err}`;
  }
  const at = clockOf(summary.endedAt);
  const base = at ? `Xong lúc ${at}` : 'Xong';
  if (stage === 2) return `${base} · ${summary.flagsCount > 0 ? `${summary.flagsCount} thẻ gắn cờ` : 'không có thẻ gắn cờ'}`;
  return base;
}

export interface RunBarView {
  stages: Array<{
    stage: RunStage; name: string; desc: string; action: ActionState; text: string; running: boolean; failed: boolean; flags: number; note?: string;
    /** 261010 P8a — CHỈ thẻ Chặng 2 khi có `gate`: dòng trạng thái duyệt + nút phụ (Chỉ duyệt / Huỷ duyệt). */
    approvalText?: string; secondary?: ActionState[];
  }>;
  stage3: string;
}
/** Dòng trạng thái duyệt dưới tiêu đề chặng 2 (chỉ GIỜ, không tên người). `undefined` khi không có `gate` (G1) hoặc không Sky. */
export function approvalTextOf(ctx: RunBarCtx): string | undefined {
  if (!ctx.isSky || !ctx.gate) return undefined;
  const at = clockOf(ctx.gate.approval?.at);
  switch (phaseOf(ctx)) {
    case 'G7': return STAGE1_RUNNING;
    case 'G2': return 'Chưa duyệt — chặng 1 chưa xong';
    case 'G4': return at ? `Đã duyệt lúc ${at}` : 'Đã duyệt';
    case 'G5': return `Kịch bản đã đổi sau lần duyệt${at ? ` lúc ${at}` : ''} — duyệt lại để chạy chặng 2`;
    default: return 'Chưa duyệt kịch bản';
  }
}
/** Ghi chú chặng 1 = các dòng `info` của lượt chạy gần nhất (nối " · ", cắt 300). Chưa có lượt nào hoặc `info` rỗng ⇒ không ghi chú (261010 P3c QĐ 8: bỏ câu cố định). */
export function stage1NoteOf(summary: RunSummary | undefined): string | undefined {
  const lines = (summary?.info ?? []).map((x) => String(x ?? '').trim()).filter(Boolean);
  return lines.length ? truncateText(lines.join(' · '), NOTE_MAX) : undefined;
}
export function runBarOf(ctx: RunBarCtx, runs?: WorkflowRuns): RunBarView {
  const actions = runStageActions(ctx);
  const runningStage = runningStageOf(ctx.jobs);
  const job = ctx.jobs.find((x) => x.type === 'FILM_WORKFLOW_RUN') as (typeof ctx.jobs[number] & { progress?: FilmRunningJob['progress'] }) | undefined;
  return {
    stages: ([1, 2] as RunStage[]).map((stage) => {
      const summary = stage === 1 ? runs?.stage1 : runs?.stage2;
      const running = runningStage === stage;
      const p = running ? job?.progress : undefined;
      return {
        stage, name: STAGE_NAME[stage], desc: stage === 2 && ctx.gate ? `${STAGE_DESC[2]} ${GATED_DESC}` : STAGE_DESC[stage], action: actions[stage - 1],
        ...(stage === 2 && ctx.gate ? { approvalText: approvalTextOf(ctx), secondary: actions.slice(2) } : {}),
        text: runSummaryText(stage, summary, { running, progressLabel: p?.label, progressNo: p ? [p.current, p.total] : undefined }),
        running, failed: !running && summary?.status === 'FAILED', flags: !running && stage === 2 && summary?.status === 'COMPLETED' ? summary.flagsCount : 0,
        ...(stage === 1 ? { note: stage1NoteOf(summary) } : {}),
      };
    }),
    stage3: STAGE3_TEXT,
  };
}

// ───────────────────────── duyệt storyboard ⇒ tự dựng prompt ─────────────────────────

const AUTO_PROMPT_REASON: Record<string, string> = {
  EDITED: 'Prompt đang sửa tay — giữ nguyên.',
  TECHNICAL: 'Video dùng prompt kỹ thuật — không tự dựng.',
  NOT_SKY: 'Plan không dùng luồng mới — không tự dựng prompt.',
  OPTED_OUT: 'Đã tắt tự dựng prompt.',
  BUSY: 'Plan đang có việc dựng prompt hoặc chạy chặng khác — chưa tự dựng; bấm "Dựng lại prompt" ở bước 7 sau khi xong.',
  ERROR: 'Không mở được việc dựng prompt — bấm "Dựng lại prompt" ở bước 7.',
};
export const AUTO_PROMPT_RUNNING_TEXT = 'Đang dựng prompt cho video này…';

/** Câu theo `autoPrompt` của response duyệt; BE cũ (không có khoá) hoặc không rõ ⇒ null. */
export function autoPromptText(ap: AutoPrompt | null | undefined): string | null {
  if (!ap || typeof ap !== 'object') return null;
  if (ap.started) return AUTO_PROMPT_RUNNING_TEXT;
  if (!ap.reason) return null;
  return AUTO_PROMPT_REASON[ap.reason] ?? 'Chưa tự dựng prompt cho video này.';
}
/** Như `autoPromptText` nhưng "đang dựng" chỉ khi job dựng prompt của video CÒN chạy; xong rồi ⇒ câu kết thúc (kết quả/lỗi xem ở bước 7 và Lịch sử). */
export function autoPromptLineOf(ap: AutoPrompt | null | undefined, authorRunning: boolean): string | null {
  const t = autoPromptText(ap);
  if (!t) return null;
  if (ap?.started && !authorRunning) return 'Việc tự dựng prompt đã kết thúc — xem kết quả ở bước 7.';
  return t;
}
